import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { createJWT } from '../utils/jwt';

export interface SmarterElementConstraints {
  minHeight?: number | string;
  maxHeight?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
}

export interface SmarterElementProps {
  /** Element type (e.g., 'exam-card', 'session-viewer') */
  type: string;
  
  /** Configuration data to pass to the element */
  config?: Record<string, any>;
  
  /** Size constraints for the iframe */
  constraints?: SmarterElementConstraints;
  
  /** Environment for element URLs */
  environment?: 'development' | 'production';
  
  /** Custom base URL for elements */
  baseUrl?: string;
  
  /** Callback when element is ready */
  onReady?: () => void;
  
  /** Callback when element resizes */
  onResize?: (dimensions: { height: number; width: number; elementId: string }) => void;
  
  /** Callback when element encounters an error */
  onError?: (error: Error) => void;
  
  /** Callback for custom events from the element */
  onEvent?: (eventType: string, data: any) => void;
  
  /** Additional CSS styles for the iframe */
  style?: React.CSSProperties;
  
  /** CSS class name for the iframe */
  className?: string;
  
  /** Whether to auto-resize to content (bypasses constraints) */
  autoResize?: boolean;
}

export interface SmarterElementRef {
  /** Update the element's configuration */
  updateConfig: (newConfig: Record<string, any>) => void;
  
  /** Update size constraints */
  updateConstraints: (newConstraints: SmarterElementConstraints) => void;
  
  /** Get current dimensions */
  getDimensions: () => { height: number; width: number } | null;
  
  /** Send custom message to element */
  sendMessage: (type: string, data?: any) => void;
  
  /** Get the iframe element */
  getIframe: () => HTMLIFrameElement | null;
}

/**
 * SmarterElement React Component
 * 
 * A declarative React component for embedding SmarterElements with automatic
 * resizing, constraints, and event handling.
 * 
 * @example
 * ```tsx
 * <SmarterElement
 *   type="exam-card"
 *   config={{ examId: '123', studentId: '456' }}
 *   constraints={{ minHeight: 200, maxHeight: 600 }}
 *   onReady={() => console.log('Element ready!')}
 *   onResize={(dims) => console.log('Resized:', dims)}
 * />
 * ```
 */
export const SmarterElement = forwardRef<SmarterElementRef, SmarterElementProps>(({
  type,
  config = {},
  constraints = {},
  environment = 'development',
  baseUrl,
  onReady,
  onResize,
  onError,
  onEvent,
  style = {},
  className = '',
  autoResize = false
}, ref) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [dimensions, setDimensions] = useState<{ height: number; width: number } | null>(null);
  const elementIdRef = useRef(`smarter-element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  // Convert constraints to CSS values
  const constraintToCSS = (value: number | string): string => {
    if (typeof value === 'number') return `${value}px`;
    return value;
  };

  // Build iframe URL
  const getIframeUrl = () => {
    const base = baseUrl || (environment === 'production' ? 'https://elements.smarterservices.com' : '/elements');
    const elementPath = type.startsWith('blockkit/') ? 'blockkit/renderer' : type;
    
    const jwt = createJWT({
      ...(type.startsWith('blockkit/') ? { blocksJson: JSON.stringify(config) } : config),
      _constraints: constraints,
      elementId: elementIdRef.current
    });

    return `${base}/${elementPath}?data=${encodeURIComponent(jwt)}`;
  };

  // Handle messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;

      try {
        const { type: messageType, ...data } = event.data;

        switch (messageType) {
          case 'resize':
            if (data.elementId === elementIdRef.current) {
              const newDimensions = { height: data.height || 0, width: data.width || 0, elementId: data.elementId };
              setDimensions({ height: newDimensions.height, width: newDimensions.width });
              onResize?.(newDimensions);
            }
            break;

          case 'ready':
            if (data.elementId === elementIdRef.current) {
              onReady?.();
            }
            break;

          case 'error':
            if (data.elementId === elementIdRef.current) {
              const error = new Error(data.message || 'Element error');
              onError?.(error);
            }
            break;

          default:
            // Custom events
            if (data.elementId === elementIdRef.current) {
              onEvent?.(messageType, data);
            }
            break;
        }
      } catch (error) {
        console.error('Error handling message from SmarterElement:', error);
        onError?.(error as Error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onReady, onResize, onError, onEvent]);

  // Expose imperative API
  useImperativeHandle(ref, () => ({
    updateConfig: (newConfig: Record<string, any>) => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'update-config',
          config: newConfig,
          elementId: elementIdRef.current
        }, '*');
      }
    },

    updateConstraints: (newConstraints: SmarterElementConstraints) => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'update-constraints',
          constraints: newConstraints,
          elementId: elementIdRef.current
        }, '*');
      }
    },

    getDimensions: () => dimensions,

    sendMessage: (type: string, data?: any) => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type,
          ...data,
          elementId: elementIdRef.current
        }, '*');
      }
    },

    getIframe: () => iframeRef.current
  }), [dimensions]);

  // Calculate iframe styles
  const iframeStyles: React.CSSProperties = {
    border: 'none',
    borderRadius: '4px',
    transition: 'height 0.15s ease-out, width 0.15s ease-out',
    ...style
  };

  // Apply width constraints
  if (autoResize) {
    iframeStyles.width = '100%';
  } else if (constraints.maxWidth) {
    iframeStyles.width = constraintToCSS(constraints.maxWidth);
    iframeStyles.maxWidth = constraintToCSS(constraints.maxWidth);
  }

  if (constraints.minWidth) {
    iframeStyles.minWidth = constraintToCSS(constraints.minWidth);
  }

  // Always ensure iframe doesn't exceed parent container
  iframeStyles.maxWidth = '100%';

  // Apply height constraints
  if (autoResize && dimensions) {
    iframeStyles.height = `${dimensions.height}px`;
  } else {
    if (constraints.maxHeight) {
      iframeStyles.maxHeight = constraintToCSS(constraints.maxHeight);
    }
    if (constraints.minHeight) {
      iframeStyles.minHeight = constraintToCSS(constraints.minHeight);
    }
    
    // Use calculated height if available and within constraints
    if (dimensions && dimensions.height > 0) {
      let calculatedHeight = dimensions.height;
      
      if (constraints.maxHeight && typeof constraints.maxHeight === 'number') {
        calculatedHeight = Math.min(calculatedHeight, constraints.maxHeight);
      }
      if (constraints.minHeight && typeof constraints.minHeight === 'number') {
        calculatedHeight = Math.max(calculatedHeight, constraints.minHeight);
      }
      
      iframeStyles.height = `${calculatedHeight}px`;
    } else if (constraints.minHeight) {
      iframeStyles.height = constraintToCSS(constraints.minHeight);
    } else {
      iframeStyles.height = '300px'; // Default height
    }
  }

  return (
    <iframe
      ref={iframeRef}
      src={getIframeUrl()}
      style={iframeStyles}
      className={className}
      title={`SmarterElement: ${type}`}
      allow="camera; microphone; fullscreen"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
    />
  );
});

SmarterElement.displayName = 'SmarterElement';

export default SmarterElement;
